import { useState, useEffect, useCallback, useRef } from 'react';
import { saveUserData } from '../firebase';

const computeNextStats = (currentStats, result, todayStr) => {
  const alreadyWonToday = currentStats.lastWinDate === todayStr;

  // Use 'en-CA' format (YYYY-MM-DD) to match the format todayStr is
  // generated with in useGameEngine.js. Using the default locale format
  // here caused yesterdayStr to never match lastWinDate, which broke
  // valid consecutive-day streaks and reset them to 1 incorrectly.
  const yesterday = new Date();
  yesterday.setDate(yesterday.getDate() - 1);
  const yesterdayStr    = yesterday.toLocaleDateString('en-CA');
  const lastWinWasRecent =
    currentStats.lastWinDate === todayStr ||
    currentStats.lastWinDate === yesterdayStr;
  const currentStreak = (() => {
    if (result === 'lost')                     return 0;
    if (result === 'won' && alreadyWonToday)   return currentStats.currentStreak;
    if (result === 'won' && lastWinWasRecent)  return currentStats.currentStreak + 1;
    return 1; // Won but streak was broken — restart from 1
  })();

  return {
    ...currentStats,
    currentStreak,
    lastWinDate: result === 'won' ? todayStr : currentStats.lastWinDate,
    history:     { ...currentStats.history, [todayStr]: result },
  };
};

export function useStats({ user } = {}) {

  const [stats, setStats] = useState(() => {
    try {
      const saved = localStorage.getItem('fishle-stats');
      return saved
        ? JSON.parse(saved)
        : { currentStreak: 0, lastWinDate: null, history: {} };
    } catch {
      return { currentStreak: 0, lastWinDate: null, history: {} };
    }
  });

  // Refs so updateStats always reads current values without
  // needing to be re-created when they change.
  const userRef  = useRef(user);
  const statsRef = useRef(stats);

  useEffect(() => { userRef.current  = user;  }, [user]);
  useEffect(() => { statsRef.current = stats; }, [stats]);

  // Persist to localStorage on every stats change.
  useEffect(() => {
    localStorage.setItem('fishle-stats', JSON.stringify(stats));
  }, [stats]);

  const updateStats = useCallback((result, todayStr) => {
    // Compute from ref — avoids stale closure without listing stats
    // as a useCallback dependency
    const nextStats = computeNextStats(statsRef.current, result, todayStr);

    // State update and Firebase write are separate — no side effects
    // inside the updater function
    setStats(nextStats);

    if (userRef.current) {
      saveUserData(userRef.current.uid, { stats: nextStats });
    }
  }, []);

  return { stats, setStats, updateStats };
}